import { useState } from 'react';
import { Map as MaplibreMap } from 'maplibre-gl';

// Importa i nuovi hook specifici
import { useLayerSynchronization } from './useLayerSynchronization';
import { useMapStyleChangeSync } from './useMapStyleChangeSync';
import { useMissingImageFallback } from './useMissingImageFallback';
import { useFeaturePopupOnClick } from './useFeaturePopupOnClick';
import { useFeatureHoverCursor } from './useFeatureHoverCursor';

/**
 * Custom hook orchestratore per gestire vari aspetti dei layer sulla mappa MapLibre.
 * Utilizza hook specifici per:
 * - Sincronizzare i layer GeoJSON dallo store alla mappa.
 * - Gestire la risincronizzazione al cambio di stile della mappa.
 * - Fornire immagini di fallback per icone mancanti.
 * - Mostrare popup informativi al click sulle feature.
 * - Cambiare il cursore su hover delle feature.
 *
 * @param map L'istanza della mappa MapLibre (o null se non ancora inizializzata).
 */
export function useMapLayers(map: MaplibreMap | null) {
    // Stato per forzare l'aggiornamento dei layer quando lo stile della mappa cambia.
    // Questo stato viene passato a useLayerSynchronization e aggiornato da useMapStyleChangeSync.
    const [styleRevision, setStyleRevision] = useState(0);

    // Callback per aggiornare la revisione dello stile, passata a useMapStyleChangeSync
    const handleStyleChange = () => {
        setStyleRevision(rev => rev + 1);
    };

    // --- Utilizzo degli Hook Specifici ---

    // 1. Sincronizzazione dei layer (dipende da styleRevision)
    useLayerSynchronization(map, styleRevision);

    // 2. Gestione del cambio di stile della mappa (aggiorna styleRevision)
    useMapStyleChangeSync(map, handleStyleChange);

    // 3. Gestione delle immagini mancanti
    useMissingImageFallback(map);

    // 4. Gestione dei popup al click
    useFeaturePopupOnClick(map);

    // 5. Gestione del cursore su hover
    useFeatureHoverCursor(map);

    // Questo hook non ritorna valori, opera direttamente sull'istanza della mappa
    // orchestrando gli altri hook.
}